import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Image from "next/image";
import Link from "next/link";
import Button from "components/Button";

const OuterBannerWrapper = styled.section`
  background-color: ${({ theme }) => theme.themeColors.primary};
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  padding-top: 3.125rem;
  text-align: center;

  ${({ theme }) => theme.mq.md`
    min-height: 42rem;
    padding: 5rem 0;
  `}

  ${({ theme }) => theme.mq.xxl`
    flex-direction: row;
    padding: 0 0 4rem 0;
    text-align: left;
  `}
`;

const InnerBannerWrapper = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  max-width: 79.5625rem;
  position: relative;
  width: 100%;

  ${({ theme }) => theme.mq.xxl`
    flex-direction: row;
    padding: 0 1.5625rem;
  `}
`;

const ContentSide = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2.5rem;
  padding: 0 1.5625rem;
  width: 100%;
  z-index: 1;

  ${({ theme }) => theme.mq.xxl`
    margin-bottom: 0;
    padding: 6.25rem 0 1.875rem 0;
    max-width: 33.375rem;
  `}
`;

const ContentTitle = styled.h3`
  color: ${({ theme }) => theme.themeColors.lightGreen3};
  font-size: ${({ theme }) => theme.fontSize(2)};
  letter-spacing: -0.005rem;
  margin-bottom: 1.25rem;

  ${({ theme }) => theme.mq.md`
    font-size: ${theme.fontSize(4)};
  `}

  ${({ theme }) => theme.mq.xxl`
    margin: .5rem 0 1rem;
  `}
`;

const ContentCallout = styled.h2`
  color: ${({ theme }) => theme.themeColors.lightGreen3};
  font-size: 2.625rem;
  letter-spacing: -0.066875rem;
  line-height: 1;
  margin: 0 auto 1.5rem;

  ${({ theme }) => theme.mq.md`
    font-size: ${theme.fontSize(7)};
    max-width: 42.75rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    font-size: 3.125rem;
    line-height: 1.125;
  `}
`;

const ContentCopy = styled.p`
  color: ${({ theme }) => theme.themeColors.lightGreen3};
  font-size: ${({ theme }) => theme.fontSize(2)};
  font-weight: 600;
  line-height: 1.3125;
  margin-bottom: 2.8125rem;

  ${({ theme }) => theme.mq.md`
    max-width: 24rem;
    margin: 0 auto 2.5rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    margin: 0 0 3.5rem;
  `}
`;

const LinkWrapper = styled.a`
  display: flex;
  margin: 0 auto;
  max-width: 20.3125rem;
  width: 100%;

  ${({ theme }) => theme.mq.md`
    margin: 0 auto 2.5rem auto;
    max-width: 12.5rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    margin: 0;
  `}
`;

const ImageSide = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  padding: 2rem 0.5rem 6rem;
  position: relative;
  width: 100%;

  ${({ theme }) => theme.mq.md`
    padding: 0 1.5625rem 1rem 1.5625rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    flex: auto;
  `}
`;

const MobileImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.375rem;
  position: relative;
  width: 100%;

  ${({ theme }) => theme.mq.md`
    left: auto;
    margin: 0 auto;
    width: 100%;
  `}

  ${({ theme }) => theme.mq.xxl`
    display: none;
  `}
`;

const MobileImage = styled(Image)`
  width: 100%;

  ${({ theme }) => theme.mq.md`
    margin: 0 auto;
  `}

  ${({ theme }) => theme.mq.xxl`
    display: none;
  `}
`;

const OuterDesktopImageWrapper = styled.div`
  display: none;
  height: 39.0625rem;
  position: absolute;
  top: 0;
  width: 77.3125rem;

  ${({ theme }) => theme.mq.xxl`
    display: inline-block;
  `}
`;

const DesktopImageWrapper = styled.div`
  left: 30rem;
  position: relative;
  top: 5.125rem;
`;

const DesktopImage = styled(Image)``;

function PageBanner({
  altText,
  buttonLink,
  buttonText,
  callOutText,
  image,
  title,
  copy,
}) {
  return (
    <OuterBannerWrapper>
      <InnerBannerWrapper>
        <ContentSide>
          <ContentTitle>{title}</ContentTitle>
          <ContentCallout>{callOutText}</ContentCallout>
          <ContentCopy>{copy}</ContentCopy>
          <Link href={buttonLink} passHref>
            <LinkWrapper>
              <Button colorType="lightGreen3" textColor="primary">
                {buttonText}
              </Button>
            </LinkWrapper>
          </Link>
        </ContentSide>
        <ImageSide>
          <MobileImageWrapper>
            <MobileImage
              alt={altText}
              height={463}
              priority
              src={image}
              width={812}
            />
          </MobileImageWrapper>
        </ImageSide>
        <OuterDesktopImageWrapper>
          <DesktopImageWrapper>
            <DesktopImage
              alt={altText}
              height={463}
              priority
              src={image}
              width={812}
            />
          </DesktopImageWrapper>
        </OuterDesktopImageWrapper>
      </InnerBannerWrapper>
    </OuterBannerWrapper>
  );
}

export default PageBanner;

PageBanner.propTypes = {
  altText: PropTypes.string,
  buttonLink: PropTypes.string,
  buttonText: PropTypes.string,
  callOutText: PropTypes.object,
  copy: PropTypes.string,
  image: PropTypes.string,
  title: PropTypes.string,
};

PageBanner.defaultProps = {
  altText: "",
  buttonLink: "/",
  buttonText: "Register Now",
  callOutText: {},
  copy: "",
  image: "",
  title: "",
};
